// Typed clients for the server proxies (spec.api §2.4 / §2.5). Both require the
// Sharee to be authenticated; the PocketBase auth token is sent in the
// Authorization header. Provider keys live only on the server — never here.
import { pb } from './pocketbase';
import type { CardExtras, Contact } from './collected';

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const token = pb.authStore.token;
  if (token) headers['Authorization'] = token;
  return headers;
}

/** The card fields this product has columns of its own for (spec.api §2.4).
 *  Deliberately NOT the whole of what a card can say — see OcrItem. */
export interface OcrFields {
  name: string;
  title: string;
  company: string;
  phone: string;
  email: string;
  website: string;
  address: string;
}

/** One labelled thing printed on a card, kept as the card itself labelled it.
 *
 *  A fixed field set cannot hold a real card: a lawyer's carries a chamber line,
 *  a direct line, a fax, a secretary's address and a bar-council number, and the
 *  person scanning it is never told what was dropped. `label` is the card's own
 *  wording ("Chamber", "Fax", "Secretary") and is the part worth trusting —
 *  `kind` is the model's guess and is sometimes wrong, which is why the confirm
 *  step lets it be re-assigned. */
export interface OcrItem {
  label: string;
  value: string;
  kind: 'name' | 'title' | 'company' | 'email' | 'phone' | 'website' | 'address' | 'social' | 'other';
  confidence: number;
}

/** A parse: the mapped subset, plus everything the card actually had. */
export interface OcrParse {
  fields: OcrFields;
  items: OcrItem[];
}

interface Envelope<T> {
  status: 'success' | 'error';
  data?: T;
  error?: { code: string; message: string };
}

/**
 * A failed request that carries the HTTP status.
 *
 * The status is the difference between "the server refused" and "the request
 * never arrived", and a caller that cannot tell them apart can only offer one
 * message for both — which is how a delete that failed because the phone was
 * offline ends up telling somebody their card is not theirs.
 *
 * `status: 0` means no answer at all (offline, DNS, aborted).
 */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function postJSON<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
  // Throws ApiError, WITH the status, because "the server refused this" and
  // "the server could not be reached" call for opposite responses and a bare
  // Error cannot tell them apart. A refusal that looked like a network failure
  // is exactly how "A name is required" reached someone as a claim that their
  // card could not be claimed. A network failure rejects at fetch() above and
  // never gets here, so anything reaching this point has a real status.
  // A non-JSON body (a proxy error page, a truncated response) is still a
  // definite answer from something; the status carries the meaning.
  const env = await res.json().catch(() => null) as Envelope<T> | null;
  if (!res.ok || env?.status !== 'success' || env?.data === undefined) {
    throw new ApiError(env?.error?.message || `Request failed (${res.status})`, res.status);
  }
  return env.data;
}

/** Result of building a card from a free-text self-description (spec.product):
 *  the extracted fields plus the standard fields still missing (ask the user). */
export interface DescribeResult {
  fields: OcrFields;
  /** Extra contacts the description mentioned — a second number, another
   *  address. Optional so a client can outlive a server that predates it. */
  items?: OcrItem[];
  missing: string[];
}

/** Build card fields from a natural-language self-description. The caller MUST
 *  confirm the fields and fill anything in `missing` before use. */
export async function describeCard(text: string): Promise<DescribeResult> {
  return postJSON<DescribeResult>('/api/v1/intake/describe', { text });
}

/** OCR a card image (base64). The caller MUST confirm the returned fields before
 *  using them (FR-1) — this only parses. */
export async function parseCard(imageBase64: string, mimeType: string): Promise<OcrParse> {
  const data = await postJSON<{ fields: OcrFields; confidence: Record<string, number>; items?: OcrItem[] }>(
    '/api/v1/ocr/parse',
    { image: imageBase64, mimeType },
  );
  // items is optional so a client can outlive a server that predates it.
  return { fields: data.fields, items: data.items ?? [] };
}

/** Publish a card so its tag/QR URL resolves when tapped (login-less, anonymous
 *  — POST /api/v1/publish). Returns the server-assigned unguessable public id.
 *  Best-effort: callers fall back to a local-only id when offline.
 *
 *  Appearance (theme/styling/photo) is published alongside the contact fields —
 *  without it a tapped card would resolve as a generic default rather than the
 *  card its owner designed. The server re-validates all three. */
export async function publishCard(card: CardPayload): Promise<{ publicId: string; editToken: string }> {
  return postJSON<{ publicId: string; editToken: string }>('/api/v1/publish', cardBody(card));
}

/** The fields a card is published/updated with. */
export interface CardPayload {
  name?: string; title?: string; company?: string; phone?: string; email?: string; website?: string;
  address?: string; industry?: string; socials?: Record<string, string>;
  extraPhones?: Contact[]; extraEmails?: Contact[];
  theme?: string; styling?: Record<string, string>; photo?: string;
  extras?: CardExtras;
  backImage?: string;
}

function cardBody(card: CardPayload) {
  return {
    name: card.name ?? '',
    title: card.title ?? '',
    company: card.company ?? '',
    phone: card.phone ?? '',
    email: card.email ?? '',
    website: card.website ?? '',
    address: card.address ?? '',
    industry: card.industry ?? '',
    extraPhones: card.extraPhones ?? undefined,
    extraEmails: card.extraEmails ?? undefined,
    socials: card.socials ?? undefined,
    theme: card.theme ?? '',
    styling: card.styling ?? undefined,
    photo: card.photo ?? '',
    extras: card.extras ?? undefined,
    backImage: card.backImage ?? '',
  };
}

/** Edit an already-published card IN PLACE, authorised by the edit token that
 *  publish returned. This is what keeps a programmed NFC tag valid: the card's
 *  public id — and so its URL — is unchanged by the edit.
 *
 *  Sends the card in full — a field left empty here is cleared on the server,
 *  which is how a detail gets removed from a published card.
 *
 *  The token is a bearer credential held only on this device; it goes in a
 *  header, never in the body or the URL (so it cannot end up in a server log or
 *  a Referer). A wrong or missing token is answered with the same 404 as an
 *  unknown card. */
export async function updateCard(
  publicId: string,
  editToken: string,
  card: CardPayload,
): Promise<{ publicId: string }> {
  const res = await fetch(`/api/v1/cards/${encodeURIComponent(publicId)}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'X-Edit-Token': editToken },
    body: JSON.stringify(cardBody(card)),
  });
  const env = (await res.json()) as Envelope<{ publicId: string }>;
  if (!res.ok || env.status !== 'success' || !env.data) {
    throw new Error(env.error?.message || `Request failed (${res.status})`);
  }
  return env.data;
}

/** One day's tap count. Days with no taps are absent, not zero-filled. */
export interface DayCount { day: string; count: number }

/** What a card's holder sees about their own card. Aggregate only — the server
 *  stores no visitor identity, so there is nothing finer to ask for. */
export interface CardStats {
  publicId: string;
  total: number; // within the requested window
  allTime: number;
  days: DayCount[]; // oldest first
}

/** Read a card's tap counts.
 *
 *  Sends BOTH proofs the server accepts, because they cover different devices:
 *  the edit token belongs to the device that published the card, and the session
 *  works anywhere once the card is claimed. Sync deliberately never copies the
 *  token, so a second phone has only the session — sending the token alone left
 *  the figures unreadable there, silently.
 *
 *  Either failing is a plain 404, the same answer as an unknown card. */
export async function cardStats(publicId: string, editToken: string, days = 30): Promise<CardStats> {
  const headers: Record<string, string> = { ...authHeaders() };
  // Omitted rather than sent empty: the server treats a blank token as absent
  // anyway, and an empty header is noise in a log.
  if (editToken) headers['X-Edit-Token'] = editToken;
  const res = await fetch(
    `/api/v1/cards/${encodeURIComponent(publicId)}/stats?days=${encodeURIComponent(String(days))}`,
    { headers },
  );
  const env = (await res.json()) as Envelope<CardStats>;
  if (!res.ok || env.status !== 'success' || !env.data) {
    throw new Error(env.error?.message || `Request failed (${res.status})`);
  }
  return env.data;
}

// --- reporting a card, and writing its tag ----------------------------------

/** The closed set of report reasons the server accepts. */
export type ReportReason = 'spam' | 'abuse' | 'impersonation' | 'illegal' | 'other';

/** Report a public card for abuse. Anonymous — no reporter identity is sent or
 *  stored — and the answer is a generic acknowledgement either way, so this
 *  cannot be used to probe whether an id exists. */
export async function reportCard(publicId: string, reason: ReportReason, details = ''): Promise<void> {
  await postJSON<{ reported: boolean }>(
    `/api/v1/report/${encodeURIComponent(publicId)}`, { reason, details });
}

/** The exact bytes to write to a physical tag, budget-checked by the server. */
export interface NfcPayload {
  writeUrl: string;
  urlBytes: number;
  fitsNtag213: boolean;
}

/** Fetch the budget-checked tag URL for a card the caller can prove they hold.
 *
 *  Sends BOTH proofs, like cardStats and for the same reason: the edit token
 *  lives only on the device that published the card, the session works on any
 *  device after a claim. Either failing is the same generic 404 as an unknown
 *  id. */
export async function nfcPayload(publicId: string, editToken: string): Promise<NfcPayload> {
  const headers: Record<string, string> = { ...authHeaders() };
  if (editToken) headers['X-Edit-Token'] = editToken;
  const res = await fetch(`/api/v1/nfc/payload/${encodeURIComponent(publicId)}`, { headers });
  const env = (await res.json()) as Envelope<NfcPayload>;
  if (!res.ok || env.status !== 'success' || !env.data) {
    throw new Error(env.error?.message || `Request failed (${res.status})`);
  }
  return env.data;
}

/** Generate a validated `styling` object from a prompt (spec.api §2.5). The
 *  server guarantees the output passes the styling allow-list. */
export async function generateTheme(prompt: string): Promise<Record<string, string>> {
  const data = await postJSON<{ styling: Record<string, string> }>('/api/v1/theme/generate', { prompt });
  return data.styling;
}

// --- identity: the card is the credential (no signup, no password) -----------

/** One way to prove a card is yours. `hint` is masked (j••••@example.com,
 *  ••••42) so a stranger holding the card learns nothing new. `available` is
 *  false when a code cannot actually be delivered there yet — a phone stays
 *  false until SMS is configured, and must not be offered. */
export interface ClaimChannel { kind: 'email' | 'phone'; hint: string; available: boolean }

/** How a card can be claimed, and which channel a code was prepared for. */
export interface ClaimStart {
  collection: string;
  channels: ClaimChannel[];
  using?: ClaimChannel;
  /** Full value of the chosen channel — already printed on the card in hand,
   *  so it discloses nothing new. Passed straight to requestOtp. */
  address?: string;
  /** How long the code stays valid, in seconds, as read from the collection
   *  that enforces it. The client counts this down rather than keeping its own
   *  copy of the number, which would drift the moment either side changed.
   *  Absent means "not reported" — show no countdown, never a dead one. */
  expiresIn?: number;
}

/** Ask whether a card can be claimed, and where its code would go. Does not
 *  send anything — requestOtp does that, via PocketBase's hardened endpoint. */
export async function claimStart(publicId: string, kind?: 'email' | 'phone'): Promise<ClaimStart> {
  // `kind` asks for a particular channel; omitted, the server picks the best one
  // it can actually deliver to (the phone first wherever SMS is configured,
  // because a texted code auto-fills and an emailed one never does). The server
  // still refuses a channel it cannot deliver to, so this is a preference, not
  // an instruction.
  return postJSON<ClaimStart>('/api/v1/claim/start', { publicId, ...(kind ? { kind } : {}) });
}

/** Send a one-time code to the card's own address. This is PocketBase's
 *  built-in endpoint: it handles code generation, expiry, reuse throttling and
 *  email-enumeration protection, so we never re-implement any of it. */
export async function requestOtp(collection: string, email: string): Promise<string> {
  const res = await pb.collection(collection).requestOTP(email);
  return res.otpId;
}

/** Exchange the code for a session. Throws if the code is wrong or expired. */
export async function authWithOtp(collection: string, otpId: string, code: string): Promise<void> {
  await pb.collection(collection).authWithOTP(otpId, code);
}

/** What `signInStart` gives back. Deliberately NOT the address the code went to:
 *  see the route's own comment. An opaque id is all a caller needs. */
export interface SignInStart {
  otpId: string;
  kind: 'phone' | 'email';
  otpDuration: number;
}

/** Start a sign-in from a typed number or address alone — the receiver view's
 *  "keep your cards" field, where there is no card to claim.
 *
 *  Unlike `claimStart` + `requestOtp`, the code is created and sent SERVER-side
 *  and only an opaque id comes back. That is the whole point: this field is on a
 *  public page, so returning the identity's login would let anyone type a number
 *  and be told the address it belongs to. */
export async function signInStart(contact: string): Promise<SignInStart> {
  return postJSON<SignInStart>('/api/v1/signin/start', { contact });
}

/** Bind the card to the now-authenticated person. */
export async function claimFinish(publicId: string): Promise<void> {
  await postJSON<{ claimed: boolean }>('/api/v1/claim/finish', { publicId });
}

/** What the server decided should happen after a card was collected.
 *  - `internal` — they already have a card here; they were notified in-app and
 *    NOTHING may be sent outside the platform.
 *  - `external` — they have no card here, so the client may offer a one-tap
 *    invitation sent from the user's OWN messaging app.
 *  - `none`     — already connected (or nothing to do); say nothing. */
export type CollectChannel = 'none' | 'internal' | 'external';

/** Record the connection and ask how (or whether) to reach the other person.
 *  Always call this before offering an invitation: an existing user must be
 *  reached in-app, never by email or SMS. */
export async function recordCollect(cardId: string, fromCardId: string): Promise<CollectChannel> {
  const data = await postJSON<{ channel: CollectChannel }>('/api/v1/collect', { cardId, fromCardId });
  return data.channel;
}

/** One of the person's own cards, as the server holds it. */
export interface SyncCard {
  publicId: string;
  name: string;
  title?: string; department?: string; company?: string; phone?: string; email?: string;
  address?: string; industry?: string;
  extraPhones?: Contact[]; extraEmails?: Contact[];
  socials?: Record<string, string>;
  theme?: string; styling?: Record<string, string>; photo?: string;
  extras?: CardExtras;
  backImage?: string;
}

/** Everything a freshly-claimed device needs to look like the original one. */
export interface SyncResult {
  cards: SyncCard[];
  /** Public ids only — the resolve route already caches each card into the
   *  offline directory, so re-sending the bodies here would duplicate it. */
  collected: string[];
}

/** Pull the signed-in person's cards and collection. Requires a session, which
 *  only the OTP claim produces. */
export async function fetchSync(): Promise<SyncResult> {
  const res = await fetch('/api/v1/me/sync', { headers: authHeaders() });
  const env = (await res.json()) as Envelope<SyncResult>;
  if (!res.ok || env.status !== 'success' || !env.data) {
    throw new Error(env.error?.message || `Request failed (${res.status})`);
  }
  return env.data;
}

// --- company cards -----------------------------------------------------------

/** A company that owns employee cards, as its admin sees it. */
/** One organisation the signed-in person belongs to. */
export interface OrgRef {
  uuid: string;
  name: string;
  role: string;
}

export interface Org {
  lockedFields?: string[];
  lockableFields?: string[];
  uuid: string;
  name: string;
  domain: string;
  verified: boolean;
  theme?: string; logo?: string;
  footer?: string; address?: string;
  sharedContacts?: Contact[];
  /** Present only for an admin of an unverified domain: the DNS record to add. */
  verifyHost?: string;
  verifyRecord?: string;
  /** Every organisation the caller belongs to. Sent only when there is more
   *  than one, so its absence means "there is nothing to switch between". */
  memberships?: OrgRef[];
}

/** One line of the company's audit log. */
export interface AuditEntry {
  action: string;
  target?: string;
  actorKind: string;
  actorEmail?: string;
  detail?: Record<string, unknown>;
  at: string;
}

/** What administrators did, newest first. Admin-only, scoped to one company. */
export async function getOrgAudit(): Promise<AuditEntry[]> {
  const res = await orgJSON<{ entries: AuditEntry[] }>('/audit', 'GET');
  return res.entries ?? [];
}

/** One employee card in the roster. */
export interface RosterEntry {
  publicId: string; name: string; title?: string; department?: string; email?: string;
  employeeId?: string; claimed: boolean; status: string;
}

/** Someone who may administer the organisation. */
/**
 * Take one of your own cards out of service.
 *
 * The card stops resolving — a tap on any tag carrying it looks exactly like a
 * card that never existed — and it cannot be undone from the app. The server
 * revokes rather than erases, so the public id is spent for ever and can never
 * be handed to somebody else.
 *
 * Authorised the same way an EDIT is, because deleting is an edit and the
 * hardest one: the edit token held by the publishing device, OR the owner's
 * session. Sending only the token is what broke this on every synced device —
 * a token is issued once, to the device that published, and sync deliberately
 * never copies it, so a second phone could open the confirmation and then had
 * nothing to send.
 */
export async function deleteCard(publicId: string, editToken?: string): Promise<void> {
  const headers: Record<string, string> = { ...authHeaders() };
  if (editToken) headers['X-Edit-Token'] = editToken;
  let res: Response;
  try {
    res = await fetch(`/api/v1/cards/${encodeURIComponent(publicId)}`, {
      method: 'DELETE',
      headers,
    });
  } catch {
    throw new ApiError('Could not reach the server.', 0);
  }
  if (!res.ok) throw new ApiError('Could not delete that card.', res.status);
}

export interface OrgAdmin {
  userId: string; email: string; name: string; isYou: boolean;
}

export async function listOrgAdmins(): Promise<OrgAdmin[]> {
  const r = await orgJSON<{ admins: OrgAdmin[] }>('/admins', 'GET');
  return r.admins ?? [];
}

export async function addOrgAdmin(email: string): Promise<OrgAdmin[]> {
  const r = await orgJSON<{ admins: OrgAdmin[] }>('/admins', 'POST', { email });
  return r.admins ?? [];
}

export async function removeOrgAdmin(userId: string): Promise<OrgAdmin[]> {
  const r = await orgJSON<{ admins: OrgAdmin[] }>(
    `/admins/${encodeURIComponent(userId)}`, 'DELETE');
  return r.admins ?? [];
}

/** How the company's cards are being used. */
export interface OrgStats {
  days: number; cards: number; active: number; unclaimed: number;
  revoked: number; taps: number; used: number; byCard: Record<string, number>;
}

export async function getOrgStats(): Promise<OrgStats | null> {
  try { return await orgJSON<OrgStats>('/stats', 'GET'); } catch { return null; }
}

/** Send one employee the link to their own card again. */
export async function remindCard(publicId: string): Promise<void> {
  await orgJSON(`/cards/${encodeURIComponent(publicId)}/remind`, 'POST');
}

export interface RosterRow {
  name: string; title?: string; department?: string; email: string; phone?: string;
  employeeId?: string;
  /** Base64 data URL only (data:image/...). The server refuses anything URL-shaped. */
  photo?: string;
}

export interface RosterResult {
  created: { publicId: string; name: string; email: string }[];
  skipped: { row: number; email: string; reason: string }[];
  /** Only ever present after a SYNC import: the org-managed cards whose emails
   *  the feed stopped listing, now revoked. */
  revoked?: { publicId: string; name: string; email: string }[];
}

/** Which organisation org calls are about, for somebody who runs more than one.
 *  Kept per device: it is a working preference, not part of the account. */
const ORG_KEY = 'smartcard-org';

/** The organisation the user last chose, or '' for the server's default (their
 *  oldest membership). */
export function activeOrg(): string {
  try { return localStorage.getItem(ORG_KEY) ?? ''; } catch { return ''; }
}

/** Choose which organisation to act on. Empty clears the choice. */
export function setActiveOrg(uuid: string): void {
  try {
    if (uuid) localStorage.setItem(ORG_KEY, uuid);
    else localStorage.removeItem(ORG_KEY);
  } catch { /* private mode — the server default still applies */ }
}

async function orgJSON<T>(path: string, method: string, body?: unknown): Promise<T> {
  const chosen = activeOrg();
  const res = await fetch(`/api/v1/org${path}`, {
    method,
    headers: chosen ? { ...authHeaders(), 'X-Org': chosen } : authHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const env = (await res.json()) as Envelope<T>;
  if (!res.ok || env.status !== 'success' || env.data === undefined) {
    throw new Error(env.error?.message || `Request failed (${res.status})`);
  }
  return env.data;
}

/** The signed-in person's organisation, or null if they have none.
 *
 *  A stored choice can go stale — they were removed from that company, or it was
 *  deleted — and the server answers a stale choice with the same generic 404 as
 *  "no organisation". Left alone that reads as "you have no company" for ever,
 *  on a device that simply remembered the wrong one. So a failed lookup drops
 *  the choice and asks once more for the default. */
export async function getOrg(): Promise<Org | null> {
  try {
    return await orgJSON<Org>('', 'GET');
  } catch {
    if (!activeOrg()) return null;
    setActiveOrg('');
    try { return await orgJSON<Org>('', 'GET'); } catch { return null; }
  }
}

export async function createOrg(name: string, domain: string): Promise<Org> {
  return orgJSON<Org>('', 'POST', { name, domain });
}

/** Ask the server to look for the DNS record now. */
export async function verifyOrgDomain(): Promise<Org> {
  return orgJSON<Org>('/verify', 'POST');
}

export async function updateOrg(patch: Partial<Org>): Promise<Org> {
  return orgJSON<Org>('', 'PATCH', patch);
}

/** Mint an API key so the company's own systems can push their roster.
 *  Returned ONCE — only its hash is stored, so it cannot be shown again. */
export async function issueOrgKey(): Promise<{ key: string; header: string }> {
  return orgJSON<{ key: string; header: string }>('/key', 'POST');
}

export async function getRoster(): Promise<RosterEntry[]> {
  return orgJSON<RosterEntry[]>('/roster', 'GET');
}

/** Create or update employee cards. With `sync` the list is AUTHORITATIVE:
 *  org-managed cards whose emails it stops listing are revoked — the server
 *  refuses an empty sync and one that would revoke most of the fleet. */
export async function importRoster(rows: RosterRow[], sync = false): Promise<RosterResult> {
  return orgJSON<RosterResult>('/roster', 'POST', { rows, sync });
}

/** Take a card out of service. The tag keeps existing, so the card is disabled
 *  rather than deleted and every resolve becomes a generic 404. */
// --- events: short-lived attendee cards + check-in (spec.event-mode) ---------

export interface OrgEvent {
  uuid: string; name: string;
  startsAt?: string; endsAt?: string; endedAt?: string;
  attendees: number;
}

export interface CheckinResult {
  checkedIn: boolean; name: string; email?: string; first: boolean; deduped: boolean;
}

export interface CheckinRow {
  publicId: string; name: string; email?: string;
  firstSeen: string; lastSeen: string; scans: number;
}

export async function listEvents(): Promise<OrgEvent[]> {
  return orgJSON<OrgEvent[]>('/events', 'GET');
}

export async function createEvent(name: string, startsAt = '', endsAt = ''): Promise<OrgEvent> {
  return orgJSON<OrgEvent>('/events', 'POST', { name, startsAt, endsAt });
}

/** Rename or reschedule an event. Fields left out are untouched; a field sent
 *  as '' is cleared (an event whose end is no longer known). */
export async function updateEvent(
  uuid: string,
  patch: { name?: string; startsAt?: string; endsAt?: string },
): Promise<OrgEvent> {
  return orgJSON<OrgEvent>(`/events/${encodeURIComponent(uuid)}`, 'PATCH', patch);
}

export async function endEvent(uuid: string): Promise<void> {
  await orgJSON(`/events/${encodeURIComponent(uuid)}/end`, 'POST');
}

export async function deleteEvent(uuid: string): Promise<void> {
  await orgJSON(`/events/${encodeURIComponent(uuid)}`, 'DELETE');
}

export async function eventRoster(uuid: string): Promise<RosterEntry[]> {
  return orgJSON<RosterEntry[]>(`/events/${encodeURIComponent(uuid)}/roster`, 'GET');
}

export async function importEventRoster(
  uuid: string, rows: RosterRow[], sync = false,
): Promise<RosterResult> {
  return orgJSON<RosterResult>(`/events/${encodeURIComponent(uuid)}/roster`, 'POST', { rows, sync });
}

export async function eventCheckin(uuid: string, publicId: string): Promise<CheckinResult> {
  return orgJSON<CheckinResult>(`/events/${encodeURIComponent(uuid)}/checkin`, 'POST', { publicId });
}

export async function eventCheckins(uuid: string): Promise<CheckinRow[]> {
  return orgJSON<CheckinRow[]>(`/events/${encodeURIComponent(uuid)}/checkins`, 'GET');
}

/** Download the attendance CSV. Fetched with credentials and handed over as a
 *  blob, because a bare <a href> would carry no session. */
export async function downloadAttendance(uuid: string, eventName: string): Promise<void> {
  const chosen = activeOrg();
  const res = await fetch(`/api/v1/org/events/${encodeURIComponent(uuid)}/checkins?format=csv`, {
    headers: chosen ? { ...authHeaders(), 'X-Org': chosen } : authHeaders(),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${eventName.replace(/[^\w-]+/g, '_') || 'attendance'}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function revokeCard(publicId: string): Promise<void> {
  await orgJSON(`/cards/${encodeURIComponent(publicId)}/revoke`, 'POST');
}

/** Hand a card to someone else WITHOUT changing its public id — the tag in
 *  circulation keeps working. */
export async function transferCard(publicId: string, email: string): Promise<void> {
  await orgJSON(`/cards/${encodeURIComponent(publicId)}/transfer`, 'POST', { email });
}

/** Who operates this deployment. Empty fields are omitted by the server, and
 *  `configured` is false until it has been told an entity AND a contact — a
 *  legal notice with nothing behind it looks like a disclosure while disclosing
 *  nothing, so the client renders none at all. */
export interface LegalNotice {
  entity?: string;
  parent?: string;
  address?: string;
  regNo?: string;
  vat?: string;
  jurisdiction?: string;
  contact?: string;
  privacyUrl?: string;
  termsUrl?: string;
  /** How an ORGANISATION opens a commercial conversation — deliberately not
   *  `contact`, which is where a person exercises a data right. Both are
   *  omitted when unset, and NEITHER is covered by `configured`: the company
   *  section stands on its own. */
  companyEmail?: string;
  companyPhone?: string;
  configured: boolean;
}

/** Read the operator's legal identity. Public and login-less: a notice you need
 *  an account to read is not a notice. */
export async function fetchLegal(): Promise<LegalNotice> {
  try {
    const res = await fetch('/api/v1/legal', { headers: authHeaders() });
    const env = (await res.json()) as Envelope<LegalNotice>;
    if (!res.ok || env.status !== 'success' || !env.data) return { configured: false };
    return env.data;
  } catch {
    // Offline, or an older server that predates the route. Showing nothing is
    // correct in both cases.
    return { configured: false };
  }
}
